import React from 'react';
import { PATH } from 'common/constants';
import type { NotificationOut } from 'service/notificationsService';
import { GenericNotification } from './renderers/GenericNotification';

type BaseNotification = NotificationOut;

export type HrisLoadSource = 'APP' | 'SFTP' | 'WORKDAY';

export type HrisLoadPayload = {
  file_id?: string;
  file_name?: string;
  file_type?: string;
  source?: HrisLoadSource;
};

export type HrisLoadFailedNotification = BaseNotification & {
  type: 'hris.load_failed';
  payload: HrisLoadPayload;
};

export type HrisLoadPartialNotification = BaseNotification & {
  type: 'hris.load_partial';
  payload: HrisLoadPayload;
};

export type HrisLoadSuccessNotification = BaseNotification & {
  type: 'hris.load_success';
  payload: HrisLoadPayload;
};

type HrisNotification = HrisLoadFailedNotification | HrisLoadPartialNotification | HrisLoadSuccessNotification;
type HrisRenderers = { [N in HrisNotification as N['type']]: (n: N) => React.ReactNode };

// Automated feeds (SFTP / Workday) have no interactive submitter, so they get passive
// copy with no "reload" action; an interactive customer upload (APP) gets the actionable
// "reload it" guidance. Wording mirrors the HRIS load emails
// (FileRepository._send_recipient_notifications) so the bell and the email stay in sync.
const isAutomated = (source?: HrisLoadSource) => source === 'SFTP' || source === 'WORKDAY';

export const hrisRenderers: HrisRenderers = {
  'hris.load_failed': (n) => (
    <GenericNotification
      title={isAutomated(n.payload?.source) ? 'HRIS feed has errors' : 'Upload has errors'}
      description={
        isAutomated(n.payload?.source)
          ? "Your latest HRIS feed couldn't be processed."
          : "Your HRIS file couldn't be processed. Review the errors and reload it."
      }
      tag={{ color: 'error', children: 'Errors' }}
      link={{ text: 'Review errors', link: PATH.DATA_MANAGEMENT }}
    />
  ),
  'hris.load_partial': (n) => (
    <GenericNotification
      title={isAutomated(n.payload?.source) ? 'HRIS feed partially loaded' : 'Upload partially loaded'}
      description={
        isAutomated(n.payload?.source)
          ? "Your latest HRIS feed was partially loaded. Review what wasn't loaded."
          : "Your HRIS file was partially loaded. Review what wasn't loaded and reload it."
      }
      tag={{ color: 'warning', children: 'Partial' }}
      link={{ text: 'Review errors', link: PATH.DATA_MANAGEMENT }}
    />
  ),
  'hris.load_success': (n) => (
    <GenericNotification
      title={isAutomated(n.payload?.source) ? 'HRIS feed loaded' : 'Upload successful'}
      description={
        isAutomated(n.payload?.source)
          ? 'Your latest HRIS feed has been loaded.'
          : 'Your HRIS file has been loaded.'
      }
      tag={{ color: 'success', children: 'Loaded' }}
      link={{ text: 'View data', link: PATH.DATA_MANAGEMENT }}
    />
  ),
};
